/**
 * Mock Data Configuration
 * Controls the mock data environment settings
 */

export interface MockConfig {
  enabled: boolean;
  delayMin: number;
  delayMax: number;
  errorRate: number;
  persistData: boolean;
}

const getMockConfig = (): MockConfig => {
  const envValue = import.meta.env.VITE_USE_MOCK_DATA;
  const localStorageValue = typeof window !== 'undefined' ? localStorage.getItem('mockDataEnabled') : null;
  const explicitlyEnabled = envValue === 'true' || (import.meta.env.DEV && localStorageValue === 'true');
  
  return {
    enabled: explicitlyEnabled,
    delayMin: parseInt(import.meta.env.VITE_MOCK_DELAY_MIN || '100', 10),
    delayMax: parseInt(import.meta.env.VITE_MOCK_DELAY_MAX || '500', 10),
    errorRate: parseFloat(import.meta.env.VITE_MOCK_ERROR_RATE || '0'),
    persistData: true, // Store mock data changes in localStorage
  };
};

// Get initial config
export const mockConfig = getMockConfig();

/**
 * Enable or disable mock mode
 */
export const setMockMode = (enabled: boolean): void => {
  if (!import.meta.env.DEV) {
    console.warn('Mock mode is disabled in MVP runtime builds.');
    return;
  }

  localStorage.setItem('mockDataEnabled', enabled.toString());
  if (enabled) {
    console.warn('⚠️ Mock Data Mode is ENABLED - All API calls will use mock data');
  } else {
    console.info('✅ Mock Data Mode is DISABLED - Using real API');
  }
  window.location.reload(); // Reload to apply changes
};

/**
 * Check if mock mode is enabled (dynamic check)
 */
export const isMockModeEnabled = (): boolean => {
  const envValue = import.meta.env.VITE_USE_MOCK_DATA;
  const localStorageValue = typeof window !== 'undefined' ? localStorage.getItem('mockDataEnabled') : null;

  if (envValue === 'true') {
    return true;
  }

  if (import.meta.env.DEV && localStorageValue !== null) {
    return localStorageValue === 'true';
  }

  return false;
};

export default mockConfig;

